import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from '../contexts/LanguageContext';
import '../styles/TermsModal.css';
import logger from '../utils/logger';

const TermsModal = ({ isOpen, onAccept, onClose, termsData }) => {
  const { t } = useTranslation();
  const [hasScrolledToBottom, setHasScrolledToBottom] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const scrollContainerRef = useRef(null);

  // Reset scroll state when modal opens
  useEffect(() => {
    if (isOpen) {
      setHasScrolledToBottom(false);
      if (scrollContainerRef.current) {
        scrollContainerRef.current.scrollTop = 0;
      }
    }
  }, [isOpen]);

  // Handle scroll to detect when user reaches bottom
  const handleScroll = () => {
    const container = scrollContainerRef.current;
    if (container) {
      const { scrollTop, scrollHeight, clientHeight } = container;
      // Consider "bottom" when user is within 50px of the end
      const isAtBottom = scrollTop + clientHeight >= scrollHeight - 50;
      if (isAtBottom && !hasScrolledToBottom) {
        setHasScrolledToBottom(true);
      }
    }
  };

  // Handle accept click
  const handleAccept = async () => {
    if (!hasScrolledToBottom || !termsData) return;
    
    setIsLoading(true);
    try {
      await onAccept({
        terms_version: termsData.version,
        terms_hash: termsData.text_hash
      });
    } catch (error) {
      logger.error('Error accepting terms:', error);
    } finally {
      setIsLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="terms-modal-overlay" data-testid="terms-modal-overlay">
      <div className="terms-modal" data-testid="terms-modal">
        <div className="terms-modal-header">
          <h2>
            <span className="terms-icon">📜</span>
            {t('terms.title', 'Terms of Service & Code of Conduct')}
          </h2>
          <p className="terms-subtitle">
            {t('terms.subtitle', 'Please read and accept our Terms of Service, Code of Conduct, and Parent/Guardian Safety Notice to continue.')}
          </p>
        </div>

        <div className="terms-modal-content"
          ref={scrollContainerRef}
          onScroll={handleScroll}
          data-testid="terms-content"
        >
          {termsData ? (
            <div className="terms-text">
              <div className="terms-meta-info">
                <div className="terms-version-badge">
                  Version {termsData.version} • Last updated: {termsData.last_updated}
                </div>
                {termsData.language && termsData.language !== 'en' && (
                  <div className="terms-language-badge">
                    {t('terms.viewingIn', 'Viewing in')}: {termsData.language.toUpperCase()}
                  </div>
                )}
              </div>
              <pre className="terms-content-text">{termsData.text}</pre>
            </div>
          ) : (
            <div className="terms-loading">
              <div className="loading-spinner"></div>
              <p>{t('common.loading', 'Loading...')}</p>
            </div>
          )}
        </div>

        <div className="terms-modal-footer">
          <div className="scroll-indicator">
            {!hasScrolledToBottom ? (
              <span className="scroll-hint">
                <span className="scroll-arrow">↓</span>
                {t('terms.scrollToRead', 'Scroll down to read all terms')}
              </span>
            ) : (
              <span className="scroll-complete">
                <span className="check-icon">✓</span>
                {t('terms.readComplete', 'You have read all terms')}
              </span>
            )}
          </div>

          <div className="terms-buttons">
            {onClose && (
              <button 
                className="terms-btn-cancel"
                onClick={onClose}
                data-testid="terms-cancel-btn"
              >
                {t('common.cancel', 'Cancel')}
              </button>
            )}
            <button 
              className={`terms-btn-accept ${hasScrolledToBottom ? 'enabled' : 'disabled'}`}
              onClick={handleAccept}
              disabled={!hasScrolledToBottom || isLoading}
              data-testid="terms-accept-btn"
            >
              {isLoading ? (
                <span className="btn-loading">
                  <span className="spinner"></span>
                  {t('common.processing', 'Processing...')}
                </span>
              ) : (
                <>
                  <span className="accept-icon">✓</span>
                  {t('terms.iAgree', 'I Agree')}
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TermsModal;
